import { ReactElement, useEffect, useMemo, useState } from 'react';
import { EntityLabel } from 'components/common/SearchInput/types';
import { makeSearchInputWithStore } from 'components/common/SearchInput';
import { useEntitySearch } from './searchStore';

export function filterInspections<
  T extends {
    code?: string;
    template?: { summary?: string };
    renderableProcess?: { code?: string };
  },
>(data: T[], searchPhrase: string): T[] {
  if (!searchPhrase || searchPhrase.trim() === '') {
    return data;
  }
  const lowerSearchPhrase = searchPhrase.toLowerCase();
  return data.filter((inspection) => {
    const code = inspection.code?.toLowerCase() || '';
    const templateSummary =
      inspection.template?.summary?.toLowerCase() || '';
    const processCode =
      inspection.renderableProcess?.code?.toLowerCase() || '';

    return (
      code.includes(lowerSearchPhrase) ||
      templateSummary.includes(lowerSearchPhrase) ||
      processCode.includes(lowerSearchPhrase)
    );
  });
}

export function useInspectionsWithSearch<
  T extends {
    code?: string;
    template?: { summary?: string };
    renderableProcess?: { code?: string };
  },
>(
  data: T[]
): {
  filteredData: T[];
  searchPhrase: string;
  InspectionSearchInput: () => ReactElement;
} {
  const { searchPhraseStore, searchPhrase: searchPhraseFromStore } =
    useEntitySearch(EntityLabel.inspection);
  const [searchPhrase, setSearchPhrase] = useState(searchPhraseFromStore);

  useEffect(() => {
    setSearchPhrase(searchPhraseFromStore);
    return searchPhraseStore.subscribe(() => {
      setSearchPhrase(searchPhraseStore.get());
    });
  }, [searchPhraseStore, searchPhraseFromStore]);

  const filteredData = useMemo(() => {
    return filterInspections(data, searchPhrase);
  }, [data, searchPhrase]);

  const InspectionSearchInput = useMemo(() => {
    return makeSearchInputWithStore(
      EntityLabel.inspection,
      searchPhraseStore
    );
  }, [searchPhraseStore]);

  return {
    filteredData,
    searchPhrase,
    InspectionSearchInput,
  };
}
